import type { UserSettingsTab } from '../useUserSettingsTab';

/**
 * The left rail — `tmpl/main.tmpl:9-45`, rendered 1:1.
 *
 * Four `<li>`s inside one `ul.nav.nav-tabs.user_settings-menu__nav`: the
 * "User Settings" title row (hamburger + collapse chevron) and the three tab
 * anchors. EVERY `<li>` carries `class="active"` in the template — a literal,
 * not state; the rail's own highlight is the `.selected` class on the `<a>`.
 * `styles.css:146-272` keys off all of these, so the class names and the
 * nesting are byte-identical to the template.
 *
 * ★ `data-toggle="tab"` IS KEPT but is INERT here.
 * On the Backbone page Bootstrap-3's delegated `click.bs.tab.data-api` handler
 * does two things to these anchors: it `preventDefault()`s them, and then calls
 * `$(this).tab('show')` — which early-returns immediately because
 * `$this.parent('li').hasClass('active')` is true for all four `<li>`s. So its
 * ONLY observable effect is suppressing the anchors' native `href` navigation.
 * React pages do not load Bootstrap's JS, so the handlers below reproduce that
 * one effect with an explicit `preventDefault()` (see each call site).
 *
 * ★ WHY THE TAB ANCHORS STILL END UP CHANGING THE HASH.
 * `activateTab` (`view/main.js:84`) calls `e.stopPropagation()`, which stops the
 * click before it reaches `document` — so on the REAL page Bootstrap never sees
 * a tab-anchor click and the native `href="#submittal"` navigation is NOT
 * suppressed. It lands on the same fragment `Backbone.history.navigate()` has
 * just written, so the two collapse into a single history entry and the net
 * result is identical to writing the hash once. `useUserSettingsTab.openTab`
 * writes it once.
 * The title anchor is different: `collapseMenu` does NOT stopPropagation, so
 * Bootstrap DOES preventDefault it and its `href="#"` never reaches the URL.
 * Hence `preventDefault()` on the title anchor and not on the tab anchors —
 * where the write is done explicitly instead.
 */
interface SettingsMenuProps {
  activeTab: UserSettingsTab;
  onOpenTab: (tab: UserSettingsTab) => void;
  /** `self.opened === false` — adds `.closed` here and `.ml-50` on `.user_settings`. */
  collapsed: boolean;
  onToggleCollapse: () => void;
}

const SettingsMenu = ({
  activeTab,
  onOpenTab,
  collapsed,
  onToggleCollapse,
}: SettingsMenuProps) => {
  // `view/main.js:80-87` activateTab. The `.selected` swap is declarative here;
  // legacy does it twice per click (once in `activateTab`, once again in
  // `changeUserSettingsAppViewState`) and both land on the same anchor.
  const tabClick = (e: React.MouseEvent<HTMLAnchorElement>, value: UserSettingsTab) => {
    // Legacy's `e.stopPropagation()` has no React equivalent to reproduce (no
    // delegated Bootstrap listener exists to stop); the hash write it lets
    // through is done explicitly by `openTab`, so the native anchor navigation
    // is suppressed to avoid writing the same fragment twice.
    e.preventDefault();
    onOpenTab(value);
  };

  // `view/main.js:88-98` collapseMenu — bound to BOTH the hamburger `<i>` INSIDE
  // the title anchor and the `.menu-collapse` chevron beside it, sharing one
  // `self.opened` flag (so either control toggles the other's state).
  // ★ The selector is `.user_settings_title .icon-hamburger`, NOT the anchor —
  // clicking the "User Settings" text or the anchor's padding does nothing.
  const collapseClick = (e: React.MouseEvent) => {
    // Also suppresses the enclosing anchor's `href="#"` navigation for the
    // hamburger case; the anchor carries its own preventDefault for the rest.
    e.preventDefault();
    onToggleCollapse();
  };

  return (
    <div className={`col-xs-12 user_settings-menu${collapsed ? ' closed' : ''}`}>
      <div className="col-xs-12 p-0">
        <div className="row p-0">
          <div className="col-xs-12">
            <ul className="nav nav-tabs user_settings-menu__nav">
              <li className="active user_settings_title-li">
                {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                <a
                  className="user_settings_title"
                  data-toggle="tab"
                  href="#"
                  onClick={(e) => e.preventDefault()} /* = Bootstrap's data-api preventDefault: `href="#"` never reaches the URL */
                >
                  {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/click-events-have-key-events */}
                  <i className="icon-hamburger" onClick={collapseClick} />
                  {/* Inert in legacy — no handler is bound to the label. */}
                  <span>User Settings</span>
                </a>
                {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/click-events-have-key-events */}
                <i
                  className="icon-angle-left ka-has-tooltip menu-collapse"
                  tooltip="Collapse"
                  onClick={collapseClick}
                />
              </li>
              <li className="active">
                <a
                  className={`user_settings_tab${activeTab === 'submittal' ? ' selected' : ''}`}
                  data-value="submittal"
                  id="submittal"
                  data-toggle="tab"
                  href="#submittal"
                  onClick={(e) => tabClick(e, 'submittal')}
                >
                  <i className="icon-baseline-description-24px" />
                  <span>Submittal</span>
                </a>
              </li>
              <li className="active">
                <a
                  className={`user_settings_tab${activeTab === 'proposal' ? ' selected' : ''}`}
                  id="proposal"
                  data-value="proposal"
                  data-toggle="tab"
                  href="#proposal"
                  onClick={(e) => tabClick(e, 'proposal')}
                >
                  <i className="material-symbols-outlined quote" />
                  <span>Proposal</span>
                </a>
              </li>
              <li className="active">
                <a
                  className={`user_settings_tab${activeTab === 'color' ? ' selected' : ''}`}
                  data-value="color"
                  id="color"
                  data-toggle="tab"
                  href="#color"
                  onClick={(e) => tabClick(e, 'color')}
                >
                  <i className="material-symbols-outlined palette" />
                  <span>Color</span>
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsMenu;
